/**
 * Admin → Provider Payouts
 * Clean rewrite — 100% DB-driven, no hardcoded amounts/labels.
 *
 * Consumes:
 *   GET  /api/admin/payout-queue            → list + metrics (default = actionable rows only)
 *   GET  /api/admin/payout-queue?status=all → include released history
 *   POST /api/admin/payouts/:id/release     → mark paid
 *   POST /api/admin/payouts/:id/hold        → put on hold
 */

import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';

type Status = 'pending' | 'processing' | 'paid' | 'on_hold' | 'disputed' | string;

interface Payout {
  id: number;
  provider_id: number;
  provider_name: string;
  provider_email: string;
  amount_gbp: string;
  amount_pence: number;
  percentage: number;
  status: Status;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Metrics {
  total_held_gbp: string;
  total_held_count: number;
  due_this_week_gbp: string;
  pending_count: number;
  released_today_gbp: string;
  released_today_count: number;
  released_week_gbp: string;
  disputed_gbp: string;
  disputed_count: number;
  providers_awaiting: number;
  scheduled_release_gbp: string;
}

const STATUS_BADGE: Record<string, string> = {
  pending:    'bg-yellow-100 text-yellow-800 border-yellow-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  paid:       'bg-green-100 text-green-800 border-green-200',
  on_hold:    'bg-orange-100 text-orange-800 border-orange-200',
  disputed:   'bg-red-100 text-red-800 border-red-200',
};

function fmtGBP(s: string | number) {
  const n = typeof s === 'number' ? s : parseFloat(String(s).replace(/[^0-9.]/g, '')) || 0;
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const AdminProviderPayoutsPage = () => {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [actionId, setActionId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.request(`/admin/payout-queue?status=${filter}`, 'GET');
      if (res.success && res.data) {
        setPayouts(Array.isArray(res.data.payouts) ? res.data.payouts : []);
        setMetrics(res.data.metrics || null);
      } else {
        setError(res.message || 'Failed to load payouts');
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  async function release(id: number) {
    if (!confirm('Mark this payout as released (paid)?')) return;
    setActionId(id);
    try {
      const r = await apiService.request(`/admin/payouts/${id}/release`, 'POST', {});
      if (r.success) await load();
      else alert('Release failed: ' + (r.message || 'unknown'));
    } catch (e: any) {
      alert('Release failed: ' + (e?.message || 'network error'));
    } finally {
      setActionId(null);
    }
  }

  async function hold(id: number) {
    const reason = prompt('Reason for placing this payout on hold?');
    if (reason === null) return;
    setActionId(id);
    try {
      const r = await apiService.request(`/admin/payouts/${id}/hold`, 'POST', { reason });
      if (r.success) await load();
      else alert('Hold failed: ' + (r.message || 'unknown'));
    } catch (e: any) {
      alert('Hold failed: ' + (e?.message || 'network error'));
    } finally {
      setActionId(null);
    }
  }

  const totalDisbursing = useMemo(
    () => payouts.filter(p => /pending|processing|on_hold/i.test(p.status))
                 .reduce((s, p) => s + (p.amount_pence || 0), 0),
    [payouts]
  );

  return (
    <>
      <Helmet><title>Provider Payouts - Virewire Admin</title></Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Provider Payouts</h1>
                <p className="text-gray-600 mt-1">Review and process provider payout requests</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="open">Open requests only</option>
                  <option value="all">Show full history</option>
                </select>
                <button
                  onClick={load}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  ↻ Refresh
                </button>
              </div>
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard label="To Disburse"        value={fmtGBP(totalDisbursing / 100)}                     sub={`${metrics?.pending_count || 0} pending + ${metrics?.total_held_count || 0} on hold`} accent="teal" />
              <MetricCard label="On Hold"            value={fmtGBP(metrics?.total_held_gbp || 0)}              sub={`${metrics?.total_held_count || 0} on hold`} accent="orange" />
              <MetricCard label="Released Today"     value={fmtGBP(metrics?.released_today_gbp || 0)}          sub={`${metrics?.released_today_count || 0} paid today`} accent="green" />
              <MetricCard label="Providers Awaiting" value={String(metrics?.providers_awaiting || 0)}          sub="distinct providers" accent="blue" />
            </div>

            {/* Status banner */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Provider</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Requested</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Paid At</th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">Loading…</td></tr>
                    ) : payouts.length === 0 ? (
                      <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        {filter === 'open' ? 'No open payout requests' : 'No payouts in history'}
                      </td></tr>
                    ) : payouts.map((p) => {
                      const actionable = /^(pending|processing|on_hold)$/i.test(p.status);
                      const busy = actionId === p.id;
                      return (
                        <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-bold text-gray-900">#{p.id}</td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-semibold text-gray-900">{p.provider_name}</p>
                            <p className="text-xs text-gray-500">{p.provider_email}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-bold text-gray-900">{fmtGBP(p.amount_gbp)}</p>
                            <p className="text-xs text-gray-500">{p.percentage}% share</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                              {String(p.status).replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-600">{fmtDate(p.created_at)}</td>
                          <td className="px-6 py-4 text-xs text-gray-600">{fmtDate(p.paid_at)}</td>
                          <td className="px-6 py-4 text-right">
                            {actionable ? (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  disabled={busy}
                                  onClick={() => release(p.id)}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                                >
                                  {busy ? 'Working…' : 'Release'}
                                </button>
                                <button
                                  disabled={busy}
                                  onClick={() => hold(p.id)}
                                  className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-xs font-bold rounded-lg disabled:opacity-50"
                                >
                                  Hold
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
                Showing {payouts.length} {filter === 'open' ? 'open' : ''} payout{payouts.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

function MetricCard({ label, value, sub, accent }: { label: string; value: string; sub: string; accent: 'teal' | 'orange' | 'green' | 'blue' }) {
  const accents = {
    teal:   'border-l-teal-500 text-teal-700',
    orange: 'border-l-orange-500 text-orange-700',
    green:  'border-l-green-500 text-green-700',
    blue:   'border-l-blue-500 text-blue-700',
  };
  return (
    <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${accents[accent].split(' ')[0]} p-5 shadow-sm`}>
      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-xs text-gray-500">{sub}</p>
    </div>
  );
};

export default AdminProviderPayoutsPage;
